// ═══════════════════════════════════════════════════════════════════
//  CourrielLecture.js
//  Colonne de droite : lecture d'un message — extrait de Courriel
// ═══════════════════════════════════════════════════════════════════

import { THEME } from "../../socle/theme";
import { MAIL_TAGS, SENTIMENT_ICON, URGENCE_COLOR } from "./constantes";
import { btnStyle, mailDate, telechargerPJ } from "./utils";
import { MailHtml } from "./MailHtml";
import { TagPill } from "./TagPill";

export function CourrielLecture({ changerTag, chargementMsg, creerTicket, deplacer, dossier, estBoiteRecue, imagesVisibles, isMobile, relancerIA, repondre, selection, setImagesVisibles, setSelection, supprimer, token }) {
  return (
    <div>
      {chargementMsg && <div style={{ color: THEME.textDim, fontSize: 13, padding: 16 }}>Ouverture du message…</div>}

      {!chargementMsg && !selection && !isMobile && (
        <div style={{ background: THEME.surface, border: `1px dashed ${THEME.border}`, borderRadius: 12,
                      padding: 40, textAlign: "center", color: THEME.textDim, fontSize: 13 }}>
          Sélectionne un message pour le lire.
        </div>
      )}

      {!chargementMsg && selection && (
        <div style={{ background: THEME.surface, border: `1px solid ${THEME.border}`, borderRadius: 12, padding: 18 }}>
          {isMobile && (
            <button onClick={() => setSelection(null)} style={{ ...btnStyle(), marginBottom: 12 }}>← Retour à la liste</button>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", gap: 10, flexWrap: "wrap", alignItems: "flex-start" }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ color: THEME.text, fontSize: 17, fontWeight: 800 }}>{selection.subject}</div>
              <div style={{ color: THEME.textMuted, fontSize: 12.5, marginTop: 4 }}>
                De <b style={{ color: THEME.accent }}>{selection.from?.name || selection.from?.address}</b>
                {selection.from?.name && <span style={{ color: THEME.textDim }}> &lt;{selection.from?.address}&gt;</span>}
                {selection.to?.length > 0 && (
                  <span> · À <b style={{ color: THEME.text }}>{selection.to.map((t) => t.name || t.address).join(", ")}</b></span>
                )}
                {" · "}{mailDate(selection.date)}
              </div>
            </div>
            {selection.tag && <TagPill tag={selection.tag} />}
          </div>

          {/* Analyse IA */}
          {selection.ia && (
            <div style={{ background: "linear-gradient(90deg, rgba(188,140,255,.13), rgba(79,142,247,.05))",
                          border: `1px solid ${THEME.purpleDim}`, borderRadius: 10, padding: "11px 13px", marginTop: 14 }}>
              <div style={{ color: THEME.purple, fontSize: 10.5, fontWeight: 800, letterSpacing: ".1em", marginBottom: 4 }}>
                ✦ ANALYSE IA
              </div>
              <div style={{ color: "#CBBFE0", fontSize: 12.5, lineHeight: 1.5 }}>
                <b>Intention :</b> {selection.ia.intention || "—"}
                {selection.ia.sentiment && <> · <b>Sentiment :</b> {SENTIMENT_ICON[selection.ia.sentiment] || ""} {selection.ia.sentiment}</>}
                {selection.ia.urgence && <> · <b>Urgence :</b> <span style={{ color: URGENCE_COLOR[selection.ia.urgence] }}>{selection.ia.urgence}</span></>}
                {selection.ia.action && <><br /><b>Action suggérée :</b> {selection.ia.action}</>}
              </div>
            </div>
          )}
          {selection.iaStatus === "echec" && (
            <div style={{ color: THEME.amber, fontSize: 12, marginTop: 10 }}>
              L'analyse IA a échoué pour ce message.{" "}
              <span onClick={() => relancerIA(selection.uid)} style={{ color: THEME.accent, cursor: "pointer", textDecoration: "underline" }}>Relancer</span>
            </div>
          )}

          {/* Images distantes */}
          {selection.imagesBloquees && !imagesVisibles && (
            <div style={{ background: THEME.amberDim, color: THEME.amber, borderRadius: 8, padding: "8px 12px",
                          marginTop: 14, fontSize: 12, display: "flex", justifyContent: "space-between", gap: 10, alignItems: "center", flexWrap: "wrap" }}>
              <span>Les images distantes sont bloquées : elles peuvent signaler l'ouverture du message à l'expéditeur.</span>
              <button onClick={() => setImagesVisibles(true)} style={{ ...btnStyle(), fontSize: 11.5 }}>Afficher les images</button>
            </div>
          )}

          {/* Corps */}
          <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${THEME.border}`,
                        color: "#C3CBD4", fontSize: 13.5, lineHeight: 1.65, overflowWrap: "anywhere" }}>
            {selection.html
              ? <MailHtml html={selection.html} imagesVisibles={imagesVisibles} />
              : <div style={{ whiteSpace: "pre-wrap" }}>{selection.text}</div>}
          </div>

          {/* Pièces jointes */}
          {selection.attachments?.length > 0 && (
            <div style={{ marginTop: 14, paddingTop: 12, borderTop: `1px solid ${THEME.border}` }}>
              <div style={{ color: THEME.textMuted, fontSize: 11, fontWeight: 800, letterSpacing: ".08em", marginBottom: 7 }}>
                PIÈCES JOINTES
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {selection.attachments.map((a) => (
                  <button key={a.index} type="button"
                          onClick={() => telechargerPJ(token, selection.uid, a, dossier)}
                          style={{ ...btnStyle(), display: "inline-block" }}>
                    📎 {a.filename} <span style={{ color: THEME.textDim }}>({Math.round((a.size || 0) / 1024)} Ko)</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Actions */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 16, paddingTop: 14, borderTop: `1px solid ${THEME.border}` }}>
            <button onClick={repondre} style={btnStyle(true)}>
              {dossier === "sent" ? "↪ Renvoyer / relancer" : "↩ Répondre"}
            </button>

            {/* Étiquettes, tickets et IA : réservés aux Reçus */}
            {estBoiteRecue && (!selection.ticketId
              ? <button onClick={() => creerTicket(selection.uid)} style={btnStyle()}>🎫 Créer un ticket</button>
              : <span style={{ ...btnStyle(), color: THEME.emerald, borderColor: THEME.emeraldDim }}>🎫 Ticket #{selection.ticketId}</span>)}

            {estBoiteRecue && (
              <select value={selection.tag || "a_trier"} onChange={(e) => changerTag(selection.uid, e.target.value)}
                      style={{ ...btnStyle(), cursor: "pointer" }}>
                {Object.keys(MAIL_TAGS).map((k) => <option key={k} value={k}>{MAIL_TAGS[k].label}</option>)}
              </select>
            )}

            {estBoiteRecue && (
              <button onClick={() => deplacer(selection.uid, "spam")}
                      title="Déplace le message dans le dossier Indésirables — il sort des Reçus et de tous les compteurs"
                      style={{ ...btnStyle(), color: THEME.amber, borderColor: THEME.amberDim }}>
                🚫 Marquer indésirable
              </button>
            )}

            {dossier === "spam" && (
              <button onClick={() => deplacer(selection.uid, "inbox")}
                      style={{ ...btnStyle(), color: THEME.emerald, borderColor: THEME.emeraldDim }}>
                ↩ Ce n'est pas un indésirable
              </button>
            )}

            {dossier === "trash" && (
              <button onClick={() => deplacer(selection.uid, "inbox")}
                      style={{ ...btnStyle(), color: THEME.emerald, borderColor: THEME.emeraldDim }}>
                ♻ Restaurer dans les Reçus
              </button>
            )}

            <button onClick={() => supprimer(selection.uid)}
                    style={{ ...btnStyle(), color: THEME.red, borderColor: THEME.redDim }}>
              {dossier === "trash" ? "🗑 Supprimer définitivement" : "🗑 Corbeille"}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
